import { useContext, useEffect, useRef } from 'react';
import { SignalRContext } from 'contexts/signalR';
import { SignalREvents } from 'constants/signalR';

// MSPPROC-19481: this used to gate registration on a global "has this event
// name EVER been registered" list (SignalRContext's `methods`), for the life
// of the whole side panel session. connection.on(event, handler) ran exactly
// once, ever, per event name, wired only to whichever component happened to
// mount first - every later component calling useSignalREvent for the same
// event (a different chat channel, a channel reopened after going back to
// the list, a different ticket entirely) saw the event already in `methods`
// and never registered a live handler of its own, so its callback was never
// wired to the connection. Fixed (matching what shipped on staging/develop
// under MSPPROC-15527) by registering/tearing down per mounted instance
// instead: HubConnection.on/.off both operate per-handler, not globally, so
// this is safe even with multiple instances briefly mounted at once.
export const useSignalREvent = (event: SignalREvents, callback: (arg: string) => void): void => {
  const { connection, established } = useContext(SignalRContext);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    if (!connection) return undefined;
    const handler = (data: string): void => { callbackRef.current(data); };
    connection.on(event, handler);
    return () => {
      connection.off(event, handler);
    };
  }, [connection, established, event]);
};
